import { styled } from 'goober';
import { route } from 'preact-router';
import CrossIcon from '../../assets/checkoutWidget/cross-icon.svg';
import Style from './style.css';
import IconButton from 'preact-material-components/IconButton';
import 'preact-material-components/IconButton/style.css';
import {
  useCheckoutContext,
  useCheckoutDispatchContext,
} from '../../storeProviders/checkoutWidgetStore';
import useInterval from '../../utils/useInterval';
import { useEffect, useState } from 'preact/hooks';
import { Fragment } from 'preact';
import { paymentFailure } from '../../alias/checkoutWidgetRoutes';
import { cancelOrderAPI } from '../../api/controllers/checkoutController';
import { useRequest } from '../../api/services/useRequest';
import type { Response } from '../../api/services/useRequest';
import type {
  CancelOrderResponseType,
  ErrorResponseType,
} from '../../api/controllers/checkoutController';
import { useCheckoutTracker } from './tracker';

const Container = styled('div')`
  width: 100%;
  background: #ffffff;
  box-shadow: 1px 1px 6px rgba(174, 174, 174, 0.25);
  padding: 8px 16px 8px 16px;
  .iconBorder {
    border-radius: 2px;
    line-height: 0;
    width: 32px;
    height: 32px;
    padding: 2px;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  .iconButton {
    color: #656565;
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0px;
    width: 30px;
    height: 30px;
    margin-right: 10px;
  }
  .iconButton i {
    font-size: 20px;
  }
  .merchantName {
    font-weight: 500;
    font-size: 16px;
    line-height: 1.5;
    color: #696b6c;
    margin-left: 5px;
  }
  .merchantLogo {
    height: 100%;
    width: 100%;
  }
`;

const MainDiv = styled('div')`
  justify-content: space-between;
  flex-wrap: nowrap;
  margin: 0px auto;
`;

const Icon = styled('div')`
  align-self: center;
`;

const RightSubContainer = styled('div')`
  padding-left: 10px;
`;

const WarningTimeContainer = styled('div')`
  position: absolute;
  left: 0px;
  color: rgb(99, 99, 99);
  top: 48px;
  width: 100%;
  padding: 4px;
  text-align: center;
  background: #fff4ec;
  font-size: 14px;
  border-radius: 0px 0px 4px 4px;
  & span {
    color: rgb(255, 108, 0);
  }
`;

export default function Header() {
  const checkoutState = useCheckoutContext();
  const checkoutDispatch = useCheckoutDispatchContext();
  const checkoutTracker = useCheckoutTracker();

  const [remainingTimeInSec, setRemainingTimeInSec] = useState(undefined);

  const cancelOrderRequest: Response<
    any,
    CancelOrderResponseType,
    ErrorResponseType,
  > = useRequest();

  const cancelOrderTimeoutRequest: Response<
    any,
    CancelOrderResponseType,
    ErrorResponseType,
  > = useRequest();

  const cancelTransaction = () => {
    cancelOrderAPI(cancelOrderRequest.makeRequest);
  };

  const sendTransactionComplete = (callbackData) => {
    checkoutDispatch({
      type: 'SEND_MESSAGE',
      payload: {
        payload: callbackData && callbackData.payload,
        signature: callbackData && callbackData.signature,
        actionName: 'onTransactionComplete',
      },
    });
  };

  useEffect(() => {
    if (cancelOrderRequest.value) {
      sendTransactionComplete(cancelOrderRequest.value.callbackData);
    } else if (cancelOrderRequest.error) {
      sendTransactionComplete(cancelOrderRequest.error.callbackData);
    }
  }, [cancelOrderRequest.value, cancelOrderRequest.error]);

  const { merchantLogo, merchantName, txnRemainingTimeInSeconds } =
    checkoutState.orderDetails || {};
  const {
    orderDetailsCompletedInSeconds,
    orderTimeoutTimestampInSeconds,
    closeWidgetOnBack,
    isInstacredOrigin,
  } = checkoutState;

  const handleClose = () => {
    checkoutState.closeIconConfig.shouldShowCancellationPopup
      ? checkoutDispatch({
          type: 'STORE_STATE',
          payload: {
            closeIconConfig: {
              ...checkoutState.closeIconConfig,
              showCancellationPopup: true,
            },
          },
        })
      : isInstacredOrigin
      ? checkoutDispatch({
          type: 'SEND_MESSAGE',
          payload: {
            payload: '',
            signature: '',
            actionName: 'onTransactionComplete',
          },
        })
      : cancelTransaction();
  };

  const handleBack = () => {
    checkoutTracker.trackClick('ec_back_clicked', undefined, {
      button_label: 'arrow_back',
    });

    isInstacredOrigin &&
      closeWidgetOnBack &&
      checkoutDispatch({
        type: 'SEND_MESSAGE',
        payload: {
          payload: '',
          signature: '',
          actionName: 'onTransactionComplete',
        },
      });

    window.history.back();
  };

  const cancelOrderOnTimeout = () => {
    cancelOrderAPI(cancelOrderTimeoutRequest.makeRequest);
  };

  const handleTimeoutFailRoute = (callbackData) => {
    checkoutDispatch({
      type: 'STORE_STATE',
      payload: {
        failureReason: {
          title: 'Payment Failed',
          message: 'Transaction timed out',
          callbackData: callbackData,
        },
      },
    });
    route(paymentFailure.path);
  };

  useEffect(() => {
    if (cancelOrderTimeoutRequest.value) {
      handleTimeoutFailRoute(cancelOrderTimeoutRequest.value.callbackData);
    } else if (cancelOrderTimeoutRequest.error) {
      handleTimeoutFailRoute(cancelOrderTimeoutRequest.error.callbackData);
    }
  }, [cancelOrderTimeoutRequest.value, cancelOrderTimeoutRequest.error]);

  const minuteInSec = 60;

  useEffect(() => {
    if (txnRemainingTimeInSeconds) {
      if (orderDetailsCompletedInSeconds) {
        setRemainingTimeInSec(
          txnRemainingTimeInSeconds - orderDetailsCompletedInSeconds,
        );
      } else {
        setRemainingTimeInSec(txnRemainingTimeInSeconds);
      }
    }
  }, [txnRemainingTimeInSeconds, orderDetailsCompletedInSeconds]);

  useInterval(
    () => {
      if (remainingTimeInSec <= 0) {
        cancelOrderOnTimeout();
      } else if (
        orderTimeoutTimestampInSeconds &&
        Math.round(Date.now() / 1000) >= orderTimeoutTimestampInSeconds
      ) {
        // sometimes when user loses focus from current tab to another tab etc. the browser stops setInterval api
        // in that we will check if pre calculated end time has been passed
        setRemainingTimeInSec(0);
        cancelOrderOnTimeout();
      } else {
        setRemainingTimeInSec((old) => old - 1);
      }
    },
    remainingTimeInSec > 0 ? 1000 : null,
  );

  const handleTimerFormat = (timerSeconds) => {
    let diff = timerSeconds;
    if (diff < 0) diff = 0;
    let m = Math.floor(diff / 60);
    let s = diff % 60;
    if (m < 10) m = '0' + m;
    if (s < 10) s = '0' + s;
    return m + ':' + s;
  };

  return (
    <Fragment>
      <Container>
        <MainDiv className={Style.flexRow}>
          <div className={Style.flexRow} style={{ alignItems: 'center' }}>
            <div
              style={{ cursor: 'pointer', marginRight: '16px' }}
              onClick={handleBack}>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='14'
                height='12'
                viewBox='0 0 14 12'
                fill='none'>
                <path
                  d='M12.6567 5.06409H2.88419L5.87527 1.47151C6.01513 1.30324 6.08242 1.0863 6.06233 0.868415C6.04224 0.650532 5.93642 0.449552 5.76815 0.309689C5.59988 0.169827 5.38294 0.102539 5.16505 0.122627C4.94717 0.142716 4.74619 0.248536 4.60633 0.416808L0.486392 5.36073C0.458674 5.40005 0.433887 5.44136 0.412233 5.48433C0.412233 5.52553 0.412233 5.55025 0.354554 5.59145C0.317206 5.68592 0.29765 5.78649 0.296875 5.88808C0.29765 5.98967 0.317206 6.09024 0.354554 6.18472C0.354554 6.22592 0.354554 6.25064 0.412233 6.29183C0.433887 6.3348 0.458674 6.37611 0.486392 6.41543L4.60633 11.3594C4.6838 11.4524 4.78082 11.5272 4.89048 11.5784C5.00013 11.6297 5.11974 11.6562 5.2408 11.656C5.43332 11.6564 5.6199 11.5893 5.76815 11.4665C5.85158 11.3973 5.92055 11.3123 5.97111 11.2165C6.02166 11.1206 6.0528 11.0157 6.06275 10.9078C6.0727 10.7999 6.06127 10.691 6.02909 10.5875C5.99692 10.484 5.94465 10.3879 5.87527 10.3047L2.88419 6.71207H12.6567C12.8752 6.71207 13.0848 6.62526 13.2393 6.47073C13.3939 6.3162 13.4807 6.10662 13.4807 5.88808C13.4807 5.66955 13.3939 5.45996 13.2393 5.30543C13.0848 5.15091 12.8752 5.06409 12.6567 5.06409Z'
                  fill='#6C6C6C'
                />
              </svg>
            </div>
            <div className={Style.flexRow} style={{ alignItems: 'center' }}>
              <div
                className='iconBorder'
                style={{
                  border: merchantLogo
                    ? '1px solid #eeeeee'
                    : '1px solid transparent',
                }}>
                {merchantLogo && (
                  <img src={merchantLogo} className='merchantLogo' />
                )}
              </div>
              <div className='merchantName'>{merchantName}</div>
            </div>
          </div>
          {checkoutState.closeIconConfig.showCloseIcon && (
            <RightSubContainer className={Style.flexRow}>
              <Icon onClick={handleClose}>
                <img
                  style={{ marginLeft: '15px', cursor: 'pointer' }}
                  src={CrossIcon}
                />
              </Icon>
            </RightSubContainer>
          )}
        </MainDiv>
        {!!remainingTimeInSec && remainingTimeInSec <= 2 * minuteInSec && (
          <WarningTimeContainer>
            <div>
              Complete your payment in{' '}
              <span style={{ fontWeight: '500' }}>
                {handleTimerFormat(remainingTimeInSec)}
              </span>
            </div>
          </WarningTimeContainer>
        )}
      </Container>
    </Fragment>
  );
}



// WEBPACK FOOTER //
// ./components/checkoutWidget/Header.js